import { RawEvent, RawEventKind, TimeStampMs } from '@fullstory/recording-api';
import { DocumentPath, FramePath, isRootFrame } from '@fullstory/renderer-shared';
import { DocumentEventTimeline } from './document_event_timeline';
import type { PageEventTimeline } from './page_event_timeline';

export function addRawEventToTimeline(pageEventTimeline: PageEventTimeline, evt: RawEvent): void {
  try {
    addRawEventToPageTimeline(pageEventTimeline, evt);
  } catch (e) {
    console.warn(
      `Unexpected exception while constructing page timeline: ${e} ` +
        `(triggered by event ${JSON.stringify(evt)})`,
    );
  }

  try {
    addRawEventToDocumentTimeline(pageEventTimeline, evt);
  } catch (e) {
    console.warn(
      `Unexpected exception while constructing document timelines: ${e} ` +
        `(triggered by event ${JSON.stringify(evt)})`,
    );
  }
}

export function addRawEventToPageTimeline(
  pageEventTimeline: PageEventTimeline,
  evt: RawEvent,
): void {
  switch (evt.Kind) {
    case RawEventKind.SET_FRAME_BASE: {
      if (isRootFrame(evt.FId, evt.PIds)) {
        const newRootDocEvents = new DocumentEvents(
          new FramePath(),
          new DocumentPath(),
          evt.Args[0],
          evt.Args[1],
        );
        pageEventTimeline.addRootDocument(evt, newRootDocEvents);
      }
      break;
    }

    case RawEventKind.BLUR:
    case RawEventKind.FOCUS: {
      pageEventTimeline.addFocusEvent(evt);
      break;
    }

    case RawEventKind.UNLOAD:
      // We deliberately ignore these for the root document so that
      // seeking all the way to the end of a session doesn't leave you
      // with a blank page.
      break;

    case RawEventKind.MOUSEDOWN:
    case RawEventKind.MOUSEUP:
    case RawEventKind.MOUSEMOVE:
      pageEventTimeline.addMouseEvent(evt, new FramePath(evt.FId, evt.PIds));
      break;

    case RawEventKind.MOUSEMOVE_CURVE:
      pageEventTimeline.addMouseCurveEvent(evt, new FramePath(evt.FId, evt.PIds));
      break;

    case RawEventKind.TOUCHSTART:
    case RawEventKind.TOUCHCANCEL:
    case RawEventKind.TOUCHEND:
    case RawEventKind.TOUCHMOVE:
      pageEventTimeline.addTouchEvent(evt, new FramePath(evt.FId, evt.PIds));
      break;

    case RawEventKind.TOUCHMOVE_CURVE:
      pageEventTimeline.addTouchCurveEvent(evt, new FramePath(evt.FId, evt.PIds));
      break;

    case RawEventKind.MEDIA_QUERY_CHANGE:
      pageEventTimeline.addMediaQueryChangeEvent(evt, new FramePath(evt.FId, evt.PIds));
      break;

    case RawEventKind.FULLSCREEN:
      pageEventTimeline.addFullscreenEvent(evt);
      break;
  }

  // Handle hover events separately, or they would be split between
  // multiple cases above.
  switch (evt.Kind) {
    case RawEventKind.MOUSEMOVE:
    case RawEventKind.MOUSEMOVE_CURVE:
      pageEventTimeline.addHoverEvent(evt);
  }
}

export function addRawEventToDocumentTimeline(
  pageEventTimeline: PageEventTimeline,
  evt: RawEvent,
): void {
  switch (evt.Kind) {
    case RawEventKind.SET_FRAME_BASE:
    case RawEventKind.UNLOAD:
    case RawEventKind.MUT_INSERT:
    case RawEventKind.MUT_REMOVE:
    case RawEventKind.MUT_ATTR:
    case RawEventKind.MUT_TEXT:
    case RawEventKind.MUT_SHADOW:
    case RawEventKind.VALUECHANGE:
    case RawEventKind.PLACEHOLDER_SIZE:
    case RawEventKind.PLAY:
    case RawEventKind.PAUSE:
    case RawEventKind.WINDOW_FOCUS:
    case RawEventKind.WINDOW_BLUR:
    case RawEventKind.PERF_ENTRY:
    case RawEventKind.SELECT:
    case RawEventKind.ADOPTED_STYLESHEETS:
    case RawEventKind.CUSTOM_ELEMENT_DEFINED:
    case RawEventKind.CSSRULE_INSERT:
    case RawEventKind.CSSRULE_DELETE:
    case RawEventKind.DIALOG:
    case RawEventKind.CSSRULE_UPDATE:
    case RawEventKind.DISABLE_STYLESHEET:
    case RawEventKind.RESET_CSS_SHEET:
    case RawEventKind.SCROLL_LAYOUT:
    case RawEventKind.SCROLL_LAYOUT_CURVE:
    case RawEventKind.RESIZE_LAYOUT:
    case RawEventKind.SCROLL_VISUAL_OFFSET:
    case RawEventKind.SCROLL_VISUAL_OFFSET_CURVE:
    case RawEventKind.RESIZE_VISUAL:
    case RawEventKind.RESIZE_VISUAL_CURVE:
    case RawEventKind.MOUSEMOVE:
    case RawEventKind.MOUSEMOVE_CURVE:
    case RawEventKind.MEDIA_QUERY_CHANGE:
    case RawEventKind.ANIMATION_CREATED:
    case RawEventKind.ANIMATION_METHOD_CALLED:
    case RawEventKind.ANIMATION_PROPERTY_SET:
    case RawEventKind.DOCUMENT_TIMELINE_CREATED:
    case RawEventKind.KEYFRAME_EFFECT_CREATED:
    case RawEventKind.KEYFRAME_EFFECT_METHOD_CALLED:
    case RawEventKind.KEYFRAME_EFFECT_PROPERTY_SET:
    case RawEventKind.REC_FEAT_SUPPORTED:
    case RawEventKind.ELEMENT_PROP:
    case RawEventKind.CANVAS_DETACHED_DIMENSION:
    case RawEventKind.CANVAS: {
      const rootDocEvents = pageEventTimeline.rootDocument.at(evt.When);
      if (!rootDocEvents) {
        console.warn(
          `Dropping event because there's no active root doc (this probably ` +
            `indicates that SET_FRAME_BASE is missing or this event is ` +
            `out-of-order with respect to it): ${JSON.stringify(evt)}`,
        );
        return;
      }

      rootDocEvents.addEvent(evt, new FramePath(evt.FId, evt.PIds));
      break;
    }
  }
}

/**
 * Properties, timelines, and events associated with a recorded document. This
 * information is used by {@link FramePlayback} to reconstruct the state of the
 * document at a particular point in time during playback.
 */
export class DocumentEvents {
  readonly documentEventTimeline = new DocumentEventTimeline();

  /**
   * RawEvents for this document which aren't part of a timeline. These are the
   * events that are played back in FramePlayback#_playEvent().
   */
  readonly events: RawEvent[] = [];

  /**
   * The timestamp for the first MUT_INSERT in this document, or
   * Number.POSITIVE_INFINITY if we haven't encountered one yet.
   */
  firstInsertMs: TimeStampMs = Number.POSITIVE_INFINITY;

  /**
   * @param framePath - The path to this document's frame from the root frame.
   * @param path - The path to this document from the root document.
   * @param baseUrl - This document's base URL.
   * @param doctype - This document's doctype. If undefined, the HTML5 doctype
   * will be used.
   */
  constructor(
    public readonly framePath: FramePath,
    public readonly path: DocumentPath,
    public readonly baseUrl: string,
    public readonly doctype: string = '<!DOCTYPE html>',
  ) {}

  /**
   * Add an event to this document or one of its descendant documents.
   * @param evt - The event to add.
   * @param framePath - The path to the frame containing the document associated
   * with this event.
   * @param depth - The current component of 'framePath'. This is used to keep
   * track of our position as we recursively descend into subdocuments.
   */
  addEvent(evt: RawEvent, framePath: FramePath, depth: number = 0): void {
    if (depth >= framePath.components.length) {
      this._appendEvent(evt);
      return;
    }

    const nextFrameId = framePath.components[depth];
    if (framePath.components.length === depth + 1) {
      if (evt.Kind === RawEventKind.SET_FRAME_BASE) {
        const newDocEvents = new DocumentEvents(
          framePath,
          this.path.extend(nextFrameId, evt.When),
          evt.Args[0],
          evt.Args[1],
        );
        this.documentEventTimeline.addSubdocument(evt, nextFrameId, newDocEvents);
        return;
      } else if (evt.Kind === RawEventKind.UNLOAD) {
        // TODO(sethf): We'd like to set the subdocument timeline's value to
        // undefined at this point, since this subdocument has unloaded and no
        // longer exists, but currently that would mean that all iframes will
        // blink out of existence at the very end of a session when the
        // top-level document unloads. We'd like to be able to distinguish
        // between an UNLOAD resulting from an iframe being explicitly navigated
        // and an UNLOAD resulting from the top-level page unloading, but that's
        // a bit tricky today, so we take no action on UNLOAD for now.
        return;
      }
    }

    const nextDocEvents = this.documentEventTimeline.subdocument.get(nextFrameId).at(evt.When);
    if (!nextDocEvents) {
      console.warn(
        `Dropping event because ${framePath}[${depth}] has no active doc: ` +
          `${JSON.stringify(evt)}`,
      );
      return;
    }

    nextDocEvents.addEvent(evt, framePath, depth + 1);
  }

  private _appendEvent(evt: RawEvent): void {
    /** helper to ensure these events are passed to FramePlayback#_playEvent() */
    const addPlayedEvent = (evt: RawEvent) => {
      // As an optimization, the recording script doesn't include 'FId' in the
      // RawEvent if its value would be zero. Normalize it to 0 here so we don't
      // need to account for that everywhere.
      if (evt.FId === undefined) {
        evt.FId = 0;
      }

      if (evt.Kind === RawEventKind.MUT_INSERT) {
        this.firstInsertMs = Math.min(this.firstInsertMs, evt.When);
      }

      this.events.push(evt);
    };

    switch (evt.Kind) {
      // TODO (SC-180137) - Add support for ANIMATION_PROPERTY_SET
      case RawEventKind.ANIMATION_CREATED:
      case RawEventKind.ANIMATION_METHOD_CALLED: {
        this.documentEventTimeline.addAnimationEvent(evt);
        break;
      }

      case RawEventKind.DIALOG: {
        this.documentEventTimeline.addDialogEvent(evt);
        // these events should be played back
        addPlayedEvent(evt);
        break;
      }

      case RawEventKind.MUT_ATTR: {
        this.documentEventTimeline.addDialogEvent(evt);
        // these events should be played back
        addPlayedEvent(evt);
        break;
      }

      case RawEventKind.KEYFRAME_EFFECT_CREATED:
      case RawEventKind.KEYFRAME_EFFECT_METHOD_CALLED:
      case RawEventKind.KEYFRAME_EFFECT_PROPERTY_SET: {
        this.documentEventTimeline.addKeyframeEffectEvent(evt);
        break;
      }

      case RawEventKind.MEDIA_QUERY_CHANGE: {
        this.documentEventTimeline.addMediaQueryChangeEvent(evt);
        break;
      }

      case RawEventKind.PERF_ENTRY: {
        this.documentEventTimeline.addPerformanceEntryEvent(evt, this.baseUrl);
        break;
      }

      case RawEventKind.RESIZE_LAYOUT: {
        this.documentEventTimeline.addLayoutResizeEvent(evt);
        break;
      }

      case RawEventKind.RESIZE_VISUAL:
      case RawEventKind.RESIZE_VISUAL_CURVE: {
        this.documentEventTimeline.addVisualResizeEvent(evt);
        break;
      }

      case RawEventKind.SCROLL_LAYOUT:
      case RawEventKind.SCROLL_LAYOUT_CURVE: {
        this.documentEventTimeline.addLayoutScrollEvent(evt);
        break;
      }

      case RawEventKind.SCROLL_VISUAL_OFFSET:
      case RawEventKind.SCROLL_VISUAL_OFFSET_CURVE: {
        this.documentEventTimeline.addVisualOffsetScrollEvent(evt);
        break;
      }

      case RawEventKind.SELECT: {
        this.documentEventTimeline.addSelectionEvent(evt);
        break;
      }

      case RawEventKind.WINDOW_BLUR:
      case RawEventKind.WINDOW_FOCUS: {
        this.documentEventTimeline.addDocumentFocusEvent(evt);
        break;
      }

      default: {
        addPlayedEvent(evt);
        break;
      }
    }
  }
}
